import type { AdminRole, EventCounts, TicketManifestRow } from '@cottage/shared';
import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { ApiError, api, getLocalAdminToken, setLocalAdminToken } from './api';

interface EventRecord {
  id: string;
  name: string;
  venue: string | null;
  status: 'draft' | 'active' | 'closed';
  total: number;
  unused: number;
  used: number;
  voided: number;
}

interface BatchRecord {
  id: string;
  name: string;
  quantity: number;
  generated_quantity: number;
  generation_status: string;
  unused: number;
  used: number;
  voided: number;
}

interface AttemptRecord {
  id: string;
  result: string;
  server_attempted_at: string;
  display_number: string | null;
  device_name: string;
}

type TicketStatusFilter = 'all' | 'unused' | 'used' | 'voided';

interface TicketRecord {
  id: string;
  display_number: string;
  manual_code: string;
  status: Exclude<TicketStatusFilter, 'all'>;
  issued_at: string;
  used_at: string | null;
  voided_at: string | null;
  batch_name: string;
  used_by_device_name: string | null;
}

interface TicketPage {
  rows: TicketRecord[];
  total: number;
  nextCursor: number | null;
}

interface DeviceRecord {
  id: string;
  name: string;
  activated_at: string;
  last_seen_at: string;
  revoked_at: string | null;
}

interface AdminSession {
  identity: string;
  role: AdminRole;
  provider: 'cloudflare-access' | 'local';
}

interface AdminUser {
  email: string;
  role: AdminRole;
  created_at: string;
  created_by: string;
  updated_at: string;
  revoked_at: string | null;
  revoked_by: string | null;
}

interface AuditRecord {
  id: string;
  actor_identity: string;
  action: string;
  target_id: string | null;
  outcome: 'succeeded' | 'rejected';
  status_code: number;
  created_at: string;
}

type Confirmation =
  | { kind: 'close-event' }
  | { kind: 'delete-draft' }
  | { kind: 'delete-event' }
  | { kind: 'reset-ticket'; ticket: TicketRecord }
  | { kind: 'remove-admin'; administrator: AdminUser }
  | { kind: 'revoke-device'; device: DeviceRecord }
  | null;

const emptyCounts: EventCounts = {
  total: 0,
  unused: 0,
  used: 0,
  voided: 0,
  acceptedAttempts: 0,
  duplicateAttempts: 0,
  invalidAttempts: 0
};

const emptyTicketPage: TicketPage = { rows: [], total: 0, nextCursor: null };
const ticketPageSize = 50;

const configuredScannerDownloadUrl: unknown = import.meta.env.VITE_SCANNER_DOWNLOAD_URL;
const publishedScannerDownloadUrl =
  'https://downloads.cottage-events.com/cottage-tickets-scanner-v1.0.4.apk';
const scannerDownloadUrl =
  typeof configuredScannerDownloadUrl === 'string' &&
  configuredScannerDownloadUrl.startsWith('https://')
    ? configuredScannerDownloadUrl
    : publishedScannerDownloadUrl;

function downloadBlob(name: string, blob: Blob): void {
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = name;
  link.click();
  URL.revokeObjectURL(link.href);
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function isSessionError(error: unknown): boolean {
  return error instanceof ApiError && ['session_expired', 'unauthorized'].includes(error.code);
}

function isAccessDenied(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'administrator_not_invited';
}

function relativeTime(value: string): string {
  const elapsedSeconds = Math.max(0, Math.floor((Date.now() - new Date(value).getTime()) / 1_000));
  if (elapsedSeconds < 10) return 'just now';
  if (elapsedSeconds < 60) return `${elapsedSeconds}s ago`;
  const minutes = Math.floor(elapsedSeconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
}

function auditActionLabel(action: string): string {
  const labels: Record<string, string> = {
    'event.created': 'Created an event draft',
    'event.updated': 'Updated an event',
    'event.deleted': 'Deleted an event',
    'event.activated': 'Activated an event',
    'event.closed': 'Closed an event',
    'batch.created': 'Created a ticket batch',
    'batch.generated': 'Generated ticket credentials',
    'ticket.voided': 'Voided a ticket',
    'ticket.reset': 'Reset an admitted ticket',
    'scanner_code.created': 'Created a scanner code',
    'scanner.revoked': 'Revoked a scanner',
    'admin_user.created': 'Added an administrator',
    'admin_user.role_changed': 'Changed an administrator role',
    'admin_user.revoked': 'Removed an administrator',
    'maintenance.cleaned': 'Ran data retention cleanup',
    'admin.changed': 'Changed administrator data'
  };
  return labels[action] ?? action.replaceAll('.', ' ');
}

function identityLabel(identity: string): string {
  return identity === 'local-admin' ? 'Local administrator' : identity;
}

function roleLabel(role: AdminRole): string {
  return role === 'super_admin' ? 'super admin' : 'admin';
}

export function App() {
  const [events, setEvents] = useState<EventRecord[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [counts, setCounts] = useState<EventCounts>(emptyCounts);
  const [batches, setBatches] = useState<BatchRecord[]>([]);
  const [attempts, setAttempts] = useState<AttemptRecord[]>([]);
  const [ticketPage, setTicketPage] = useState<TicketPage>(emptyTicketPage);
  const [ticketCursor, setTicketCursor] = useState(0);
  const [ticketStatus, setTicketStatus] = useState<TicketStatusFilter>('all');
  const [ticketSearch, setTicketSearch] = useState('');
  const [ticketQuery, setTicketQuery] = useState('');
  const [ticketLoading, setTicketLoading] = useState(false);
  const [devices, setDevices] = useState<DeviceRecord[]>([]);
  const [adminSession, setAdminSession] = useState<AdminSession | null>(null);
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [auditRecords, setAuditRecords] = useState<AuditRecord[]>([]);
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [online, setOnline] = useState(() => navigator.onLine);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [confirmation, setConfirmation] = useState<Confirmation>(null);
  const [deleteConfirmationName, setDeleteConfirmationName] = useState('');
  const [localToken, setToken] = useState(getLocalAdminToken());
  const [activationCode, setActivationCode] = useState('');
  const [activationExpiresAt, setActivationExpiresAt] = useState('');
  const selected = events.find((event) => event.id === selectedId);

  const refreshEvents = useCallback(async () => {
    const data = await api<EventRecord[]>('/api/v1/admin/events');
    setEvents(data);
    setSelectedId((current) =>
      data.some((event) => event.id === current) ? current : (data[0]?.id ?? '')
    );
    setLastUpdatedAt(new Date());
  }, []);

  const refreshAdminContext = useCallback(async () => {
    const session = await api<AdminSession>('/api/v1/admin/session');
    const [audit, users] = await Promise.all([
      api<AuditRecord[]>('/api/v1/admin/audit?limit=20'),
      session.role === 'super_admin' ? api<AdminUser[]>('/api/v1/admin/users') : Promise.resolve([])
    ]);
    setAdminSession(session);
    setAuditRecords(audit);
    setAdminUsers(users);
  }, []);

  const refreshWorkspace = useCallback(async () => {
    if (!selectedId) {
      setCounts(emptyCounts);
      setBatches([]);
      setAttempts([]);
      setDevices([]);
      return;
    }
    const [nextCounts, nextBatches, nextAttempts, nextDevices] = await Promise.all([
      api<EventCounts>(`/api/v1/admin/events/${selectedId}/stats`),
      api<BatchRecord[]>(`/api/v1/admin/events/${selectedId}/batches`),
      api<AttemptRecord[]>(`/api/v1/admin/events/${selectedId}/attempts?limit=50`),
      api<DeviceRecord[]>(`/api/v1/admin/events/${selectedId}/devices`)
    ]);
    setCounts(nextCounts);
    setBatches(nextBatches);
    setAttempts(nextAttempts);
    setDevices(nextDevices);
    setLastUpdatedAt(new Date());
    setSessionExpired(false);
  }, [selectedId]);

  const refreshTickets = useCallback(async () => {
    if (!selectedId || adminSession?.role !== 'super_admin') {
      setTicketPage(emptyTicketPage);
      return;
    }
    setTicketLoading(true);
    try {
      const parameters = new URLSearchParams({
        cursor: String(ticketCursor),
        limit: String(ticketPageSize),
        status: ticketStatus
      });
      if (ticketQuery) parameters.set('q', ticketQuery);
      setTicketPage(
        await api<TicketPage>(`/api/v1/admin/events/${selectedId}/tickets?${parameters.toString()}`)
      );
    } finally {
      setTicketLoading(false);
    }
  }, [adminSession?.role, selectedId, ticketCursor, ticketQuery, ticketStatus]);

  useEffect(() => {
    if (!localToken && import.meta.env.DEV) return;
    setLoading(true);
    Promise.all([refreshEvents(), refreshAdminContext()])
      .catch((error: unknown) => {
        setNotice(errorMessage(error, 'Unable to load events'));
        setSessionExpired(isSessionError(error));
        setAccessDenied(isAccessDenied(error));
      })
      .finally(() => setLoading(false));
  }, [localToken, refreshAdminContext, refreshEvents]);

  useEffect(() => {
    refreshWorkspace().catch((error: unknown) => {
      setNotice(errorMessage(error, 'Unable to refresh workspace'));
      setSessionExpired(isSessionError(error));
    });
    const timer = window.setInterval(() => {
      if (!document.hidden && navigator.onLine) {
        void refreshWorkspace().catch((error: unknown) => {
          setNotice(errorMessage(error, 'Live refresh paused'));
          setSessionExpired(isSessionError(error));
        });
      }
    }, 15_000);
    return () => window.clearInterval(timer);
  }, [refreshWorkspace]);

  useEffect(() => {
    void refreshTickets().catch((error: unknown) => {
      setNotice(errorMessage(error, 'Unable to load tickets'));
      setSessionExpired(isSessionError(error));
    });
  }, [refreshTickets]);

  useEffect(() => {
    setTicketCursor(0);
    setTicketStatus('all');
    setTicketSearch('');
    setTicketQuery('');
  }, [selectedId]);

  useEffect(() => {
    const updateConnection = () => setOnline(navigator.onLine);
    window.addEventListener('online', updateConnection);
    window.addEventListener('offline', updateConnection);
    return () => {
      window.removeEventListener('online', updateConnection);
      window.removeEventListener('offline', updateConnection);
    };
  }, []);

  async function manualRefresh() {
    setRefreshing(true);
    try {
      await Promise.all([
        refreshEvents(),
        refreshWorkspace(),
        refreshTickets(),
        refreshAdminContext()
      ]);
      setAccessDenied(false);
      setNotice('Dashboard is up to date.');
    } catch (error) {
      setNotice(errorMessage(error, 'Unable to refresh workspace'));
      setSessionExpired(isSessionError(error));
    } finally {
      setRefreshing(false);
    }
  }

  function logOut() {
    if (adminSession?.provider === 'cloudflare-access') {
      window.location.assign('/cdn-cgi/access/logout');
      return;
    }

    setLocalAdminToken('');
    setToken('');
    setAdminSession(null);
  }

  async function createEvent(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    setBusy(true);
    try {
      const created = await api<{ id: string }>('/api/v1/admin/events', {
        method: 'POST',
        body: JSON.stringify({
          name: form.get('name'),
          venue: form.get('venue') || null
        })
      });
      formElement.reset();
      await Promise.all([refreshEvents(), refreshAdminContext()]);
      setSelectedId(created.id);
      setNotice('Event created in draft.');
    } catch (error) {
      setNotice(errorMessage(error, 'Event creation failed'));
      setSessionExpired(isSessionError(error));
    } finally {
      setBusy(false);
    }
  }

  async function changeEvent(action: 'activate' | 'close') {
    if (!selectedId) return;
    setBusy(true);
    try {
      await api(`/api/v1/admin/events/${selectedId}/${action}`, { method: 'POST' });
      await Promise.all([refreshEvents(), refreshAdminContext()]);
      setNotice(action === 'activate' ? 'Event is live.' : 'Event is closed.');
    } catch (error) {
      setNotice(errorMessage(error, 'Event update failed'));
      setSessionExpired(isSessionError(error));
    } finally {
      setBusy(false);
    }
  }

  async function deleteEvent() {
    if (!selectedId || !selected) return;
    setBusy(true);
    try {
      const deleted = await api<{ name: string; deletedTickets: number }>(
        `/api/v1/admin/events/${selectedId}`,
        { method: 'DELETE' }
      );
      setActivationCode('');
      setActivationExpiresAt('');
      await Promise.all([refreshEvents(), refreshAdminContext()]);
      setNotice(
        `${deleted.name} was deleted permanently${
          deleted.deletedTickets > 0
            ? ` with ${deleted.deletedTickets.toLocaleString()} unused tickets`
            : ''
        }.`
      );
    } catch (error) {
      setNotice(errorMessage(error, 'Unable to delete the event'));
      setSessionExpired(isSessionError(error));
    } finally {
      setBusy(false);
      setConfirmation(null);
      setDeleteConfirmationName('');
    }
  }

  async function addAdministrator(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    setBusy(true);
    try {
      const emailValue = form.get('email');
      const roleValue = form.get('role');
      const email = typeof emailValue === 'string' ? emailValue.trim() : '';
      const role: AdminRole = roleValue === 'super_admin' ? 'super_admin' : 'admin';
      await api('/api/v1/admin/users', {
        method: 'POST',
        body: JSON.stringify({ email, role })
      });
      formElement.reset();
      await refreshAdminContext();
      setNotice(`${email.toLowerCase()} can now sign in as ${roleLabel(role)}.`);
    } catch (error) {
      setNotice(errorMessage(error, 'Unable to add the administrator'));
      setSessionExpired(isSessionError(error));
    } finally {
      setBusy(false);
    }
  }

  async function changeAdministratorRole(administrator: AdminUser, role: AdminRole) {
    setBusy(true);
    try {
      await api(`/api/v1/admin/users/${encodeURIComponent(administrator.email)}`, {
        method: 'PATCH',
        body: JSON.stringify({ role })
      });
      await refreshAdminContext();
      setNotice(`${administrator.email} is now ${roleLabel(role)}.`);
    } catch (error) {
      setNotice(errorMessage(error, 'Unable to change the administrator role'));
      setSessionExpired(isSessionError(error));
    } finally {
      setBusy(false);
    }
  }

  async function removeAdministrator(administrator: AdminUser) {
    setBusy(true);
    try {
      await api(`/api/v1/admin/users/${encodeURIComponent(administrator.email)}`, {
        method: 'DELETE'
      });
      await refreshAdminContext();
      setNotice(`${administrator.email} can no longer access Cottage Tickets.`);
    } catch (error) {
      setNotice(errorMessage(error, 'Unable to remove the administrator'));
      setSessionExpired(isSessionError(error));
    } finally {
      setBusy(false);
      setConfirmation(null);
    }
  }

  async function createBatch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedId) return;
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    setBusy(true);
    try {
      const batch = await api<{ id: string; quantity: number; chunkSize: number }>(
        `/api/v1/admin/events/${selectedId}/batches`,
        {
          method: 'POST',
          body: JSON.stringify({
            name: form.get('name'),
            quantity: Number(form.get('quantity')),
            clientRequestId: crypto.randomUUID()
          })
        }
      );
      const chunks = Math.ceil(batch.quantity / batch.chunkSize);
      for (let chunkIndex = 0; chunkIndex < chunks; chunkIndex += 1) {
        await api(`/api/v1/admin/batches/${batch.id}/generate`, {
          method: 'POST',
          body: JSON.stringify({ chunkIndex })
        });
        setNotice(
          `Generating tickets… ${Math.min((chunkIndex + 1) * batch.chunkSize, batch.quantity)}/${batch.quantity}`
        );
      }
      formElement.reset();
      await Promise.all([refreshWorkspace(), refreshAdminContext()]);
      setNotice(`${batch.quantity.toLocaleString()} tickets generated and committed.`);
    } catch (error) {
      setNotice(errorMessage(error, 'Batch generation failed'));
      setSessionExpired(isSessionError(error));
    } finally {
      setBusy(false);
    }
  }

  async function createActivationCode() {
    if (!selectedId) return;
    setBusy(true);
    try {
      const result = await api<{ code: string; expiresAt: string }>(
        '/api/v1/admin/activation-codes',
        {
          method: 'POST',
          body: JSON.stringify({ eventId: selectedId, expiresInMinutes: 30 })
        }
      );
      setActivationCode(result.code);
      setActivationExpiresAt(result.expiresAt);
      await refreshAdminContext();
      setNotice(`Activation code expires ${new Date(result.expiresAt).toLocaleTimeString()}.`);
    } catch (error) {
      setNotice(errorMessage(error, 'Unable to create an activation code'));
      setSessionExpired(isSessionError(error));
    } finally {
      setBusy(false);
    }
  }

  async function revokeDevice(device: DeviceRecord) {
    setBusy(true);
    try {
      await api(`/api/v1/admin/devices/${device.id}/revoke`, {
        method: 'POST'
      });
      await Promise.all([refreshWorkspace(), refreshAdminContext()]);
      setNotice(`${device.name} was revoked and can no longer scan tickets.`);
    } catch (error) {
      setNotice(errorMessage(error, 'Unable to revoke the scanner'));
      setSessionExpired(isSessionError(error));
    } finally {
      setBusy(false);
      setConfirmation(null);
    }
  }

  async function resetTicket(ticket: TicketRecord) {
    setBusy(true);
    try {
      await api(`/api/v1/admin/tickets/${ticket.id}/reset`, { method: 'POST' });
      await Promise.all([
        refreshEvents(),
        refreshWorkspace(),
        refreshTickets(),
        refreshAdminContext()
      ]);
      setNotice(`Ticket #${ticket.display_number} can be admitted again.`);
    } catch (error) {
      setNotice(errorMessage(error, 'Unable to reset the ticket'));
      setSessionExpired(isSessionError(error));
    } finally {
      setBusy(false);
      setConfirmation(null);
    }
  }

  async function loadTicketExportRows(batch: BatchRecord): Promise<TicketManifestRow[]> {
    const tickets: TicketManifestRow[] = [];
    let cursor: number | null = 0;
    while (cursor !== null) {
      const page: { rows: TicketManifestRow[]; nextCursor: number | null } = await api(
        `/api/v1/admin/batches/${batch.id}/manifest?cursor=${cursor}&limit=1000`
      );
      tickets.push(...page.rows);
      cursor = page.nextCursor;
    }
    return tickets;
  }

  async function exportBatch(
    batch: BatchRecord,
    format: 'pdf' | 'pdf-a4-four-up' | 'seller-form' | 'png' | 'svg'
  ) {
    setBusy(true);
    try {
      const isPdf = format === 'pdf' || format === 'pdf-a4-four-up' || format === 'seller-form';
      setNotice(
        `Preparing ${format === 'pdf-a4-four-up' ? 'A4 four-up PDF' : format === 'seller-form' ? 'seller form' : isPdf ? 'PDF' : `${format.toUpperCase()} ZIP`}…`
      );
      const {
        batchExportStem,
        browserExportLimit,
        createQrZip,
        createSellerFormPdf,
        createTicketPdf
      } = await import('./browser-exports');
      if (batch.generated_quantity > browserExportLimit) {
        throw new Error(
          `This browser can prepare up to ${browserExportLimit.toLocaleString()} tickets at once. Split larger print runs into smaller batches.`
        );
      }
      const tickets = await loadTicketExportRows(batch);
      const stem = batchExportStem(tickets);
      if (format === 'pdf-a4-four-up') {
        downloadBlob(
          `${stem}-a4-four-tickets-per-sheet.pdf`,
          await createTicketPdf(tickets, 'a4-four-up')
        );
      } else if (format === 'seller-form') {
        downloadBlob(`${stem}-seller-form.pdf`, await createSellerFormPdf(tickets));
      } else if (format === 'pdf') {
        downloadBlob(`${stem}-ticket-sheets.pdf`, await createTicketPdf(tickets, 'compact'));
      } else {
        downloadBlob(`${stem}-${format}.zip`, await createQrZip(tickets, format));
      }
      setNotice(
        `Downloaded ${tickets.length.toLocaleString()} ${format === 'pdf-a4-four-up' ? 'tickets on A4 four-up sheets' : format === 'seller-form' ? 'numbered rows on a printable seller form' : format === 'pdf' ? 'compact print-ready tickets' : `${format.toUpperCase()} QR codes`}.`
      );
    } catch (error) {
      setNotice(errorMessage(error, 'Ticket export failed'));
      setSessionExpired(isSessionError(error));
    } finally {
      setBusy(false);
    }
  }

  if (!localToken && import.meta.env.DEV) {
    return (
      <main className="auth-shell">
        <section className="auth-panel">
          <p className="eyebrow">Local development</p>
          <h1>Cottage Tickets</h1>
          <p>
            Enter the explicit local administrator token from <code>apps/api/.dev.vars</code>.
          </p>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              const value = new FormData(event.currentTarget).get('token');
              if (typeof value === 'string') {
                setLocalAdminToken(value);
                setToken(value);
              }
            }}
          >
            <label>
              Administrator token
              <input name="token" type="password" minLength={32} required autoComplete="off" />
            </label>
            <button type="submit">Open operations</button>
          </form>
        </section>
      </main>
    );
  }

  if (accessDenied) {
    return (
      <main className="auth-shell">
        <section className="auth-panel">
          <p className="eyebrow">Access not enabled</p>
          <h1>Email not added</h1>
          <p>
            This email has not been added by a Cottage Tickets super admin. Ask them to add it, then
            sign in again.
          </p>
          <button type="button" onClick={() => window.location.assign('/cdn-cgi/access/logout')}>
            Sign out and use another email
          </button>
        </section>
      </main>
    );
  }

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand-lockup">
          <div>
            <p className="eyebrow">Admission control</p>
            <h1>
              Cottage
              <br />
              Tickets
            </h1>
          </div>
          <span className="environment-label">Secure organiser</span>
        </div>

        <nav aria-label="Events">
          <div className="nav-heading">
            <p className="nav-label">Events</p>
            <span>{events.length}</span>
          </div>
          {events.map((event) => (
            <button
              className={event.id === selectedId ? 'event-link active' : 'event-link'}
              key={event.id}
              aria-current={event.id === selectedId ? 'page' : undefined}
              onClick={() => {
                setSelectedId(event.id);
                setActivationCode('');
                setActivationExpiresAt('');
              }}
            >
              <span>{event.name}</span>
              <small>{event.status}</small>
            </button>
          ))}
          {!loading && events.length === 0 && (
            <p className="sidebar-empty">No events yet. Create a draft below to get started.</p>
          )}
        </nav>

        <form className="new-event" onSubmit={(event) => void createEvent(event)}>
          <p className="nav-label">Create an event</p>
          <label>
            Event name
            <input name="name" placeholder="Event name" maxLength={160} required />
          </label>
          <label>
            Venue
            <input name="venue" placeholder="Venue (optional)" maxLength={240} />
          </label>
          <button className="quiet-button" type="submit" disabled={busy}>
            Create draft
          </button>
        </form>

        {adminSession && (
          <section className="admin-account" aria-label="Administrator account">
            <div>
              <span>Signed in as</span>
              <strong title={adminSession.identity}>{identityLabel(adminSession.identity)}</strong>
              <small>{roleLabel(adminSession.role)}</small>
            </div>
          </section>
        )}
      </aside>

      <main className="workspace">
        <header className="workspace-header">
          <div>
            <p className="eyebrow">{selected?.venue || 'Event operations'}</p>
            <h2>{selected?.name || 'Create an event'}</h2>
            <div className="freshness" aria-live="polite">
              <span className={online ? 'connection online' : 'connection offline'}>
                {online ? 'Live connection' : 'Offline'}
              </span>
              {lastUpdatedAt && <span>Updated {lastUpdatedAt.toLocaleTimeString()}</span>}
            </div>
          </div>
          <div className="header-actions">
            <span className={`status-dot ${selected?.status ?? 'draft'}`}>
              {selected?.status ?? 'No event'}
            </span>
            {selected?.status === 'draft' && (
              <>
                <button onClick={() => void changeEvent('activate')} disabled={busy}>
                  Activate
                </button>
                <button
                  className="danger-button"
                  onClick={() => {
                    setDeleteConfirmationName('');
                    setConfirmation({ kind: 'delete-draft' });
                  }}
                  disabled={busy}
                >
                  Delete draft
                </button>
              </>
            )}
            {selected?.status === 'active' && (
              <>
                <button
                  className="danger-button"
                  onClick={() => setConfirmation({ kind: 'close-event' })}
                  disabled={busy}
                >
                  Close event
                </button>
                {adminSession?.role === 'super_admin' && (
                  <button
                    className="danger-button"
                    onClick={() => {
                      setDeleteConfirmationName('');
                      setConfirmation({ kind: 'delete-event' });
                    }}
                    disabled={busy}
                  >
                    Delete event
                  </button>
                )}
              </>
            )}
            {selected?.status === 'closed' && adminSession?.role === 'super_admin' && (
              <button
                className="danger-button"
                onClick={() => {
                  setDeleteConfirmationName('');
                  setConfirmation({ kind: 'delete-event' });
                }}
                disabled={busy}
              >
                Delete event
              </button>
            )}
            <button
              className="quiet-button"
              onClick={() => void manualRefresh()}
              disabled={refreshing}
            >
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
            {adminSession && (
              <button
                className="quiet-button logout-button"
                type="button"
                aria-label={`Log out ${identityLabel(adminSession.identity)}`}
                onClick={logOut}
              >
                Log out
              </button>
            )}
          </div>
        </header>

        {sessionExpired && (
          <section className="session-banner" role="alert">
            <div>
              <strong>Your secure session needs to be renewed.</strong>
              <span>Sign in again before making any operational changes.</span>
            </div>
            <button
              onClick={() =>
                window.location.assign(`/organiser/?renew=${encodeURIComponent(Date.now())}`)
              }
            >
              Sign in again
            </button>
          </section>
        )}

        {notice && (
          <div className="notice" role="status">
            {notice}
            <button aria-label="Dismiss notification" onClick={() => setNotice('')}>
              Dismiss
            </button>
          </div>
        )}

        {!selected && !loading ? (
          <section className="first-run">
            <p className="eyebrow">First event</p>
            <h3>Start with a draft</h3>
            <p>
              Name the event and venue in the form on the left. You can prepare ticket batches
              before activating admissions.
            </p>
          </section>
        ) : (
          <>
            <section className="metrics" aria-label="Ticket totals" aria-busy={loading}>
              {[
                ['Total tickets', counts.total],
                ['Ready to admit', counts.unused],
                ['Admitted', counts.used],
                ['Voided', counts.voided]
              ].map(([label, value]) => (
                <div className="metric" key={label}>
                  <span>{label}</span>
                  <strong>{loading ? '—' : Number(value).toLocaleString()}</strong>
                </div>
              ))}
            </section>

            <div className="workspace-grid">
              <section className="primary-column">
                <section className="operation-section activity-section">
                  <div className="section-heading">
                    <div>
                      <p className="eyebrow">Entrance now</p>
                      <h3>Recent scan activity</h3>
                      <p className="supporting">Newest server-validated attempts appear first.</p>
                    </div>
                    <div className="attempt-totals" aria-label="Attempt totals">
                      <span>{counts.acceptedAttempts} valid</span>
                      <span>{counts.duplicateAttempts} duplicate</span>
                      <span>{counts.invalidAttempts} invalid</span>
                    </div>
                  </div>
                  <div className="attempt-list" aria-live="polite">
                    {attempts.map((attempt) => (
                      <div className="attempt-row" key={attempt.id}>
                        <span className={`result-mark ${attempt.result}`} aria-hidden="true" />
                        <strong>{attempt.result.replaceAll('_', ' ')}</strong>
                        <span>
                          {attempt.display_number
                            ? `Ticket ${attempt.display_number}`
                            : 'No ticket'}
                        </span>
                        <span>{attempt.device_name}</span>
                        <time
                          dateTime={attempt.server_attempted_at}
                          title={new Date(attempt.server_attempted_at).toLocaleString()}
                        >
                          {relativeTime(attempt.server_attempted_at)}
                        </time>
                      </div>
                    ))}
                    {!attempts.length && (
                      <div className="empty-state">
                        <strong>No scans yet</strong>
                        <span>
                          Validated admission attempts will appear here once an entrance scanner is
                          active.
                        </span>
                      </div>
                    )}
                  </div>
                </section>

                {adminSession?.role === 'super_admin' && (
                  <section
                    className="operation-section ticket-ledger"
                    aria-labelledby="ticket-ledger-title"
                  >
                    <div className="section-heading">
                      <div>
                        <p className="eyebrow">Super admin</p>
                        <h3 id="ticket-ledger-title">All tickets</h3>
                        <p className="supporting">
                          Review every issued ticket and correct an accidental admission without
                          removing its scan history.
                        </p>
                      </div>
                      <span className="ticket-count" aria-live="polite">
                        {ticketPage.total.toLocaleString()} tickets
                      </span>
                    </div>
                    <form
                      className="ticket-toolbar"
                      role="search"
                      onSubmit={(event) => {
                        event.preventDefault();
                        setTicketCursor(0);
                        setTicketQuery(ticketSearch.trim());
                      }}
                    >
                      <label>
                        Find ticket or batch
                        <input
                          type="search"
                          value={ticketSearch}
                          onChange={(event) => setTicketSearch(event.target.value)}
                          placeholder="Ticket number or batch"
                          maxLength={80}
                        />
                      </label>
                      <label>
                        Status
                        <select
                          value={ticketStatus}
                          onChange={(event) => {
                            setTicketCursor(0);
                            setTicketStatus(event.target.value as TicketStatusFilter);
                          }}
                        >
                          <option value="all">All statuses</option>
                          <option value="unused">Ready to admit</option>
                          <option value="used">Admitted</option>
                          <option value="voided">Voided</option>
                        </select>
                      </label>
                      <button className="quiet-button" type="submit" disabled={ticketLoading}>
                        {ticketLoading ? 'Loading…' : 'Search'}
                      </button>
                    </form>
                    <div className="table-wrap">
                      <table className="ticket-table">
                        <thead>
                          <tr>
                            <th scope="col">Ticket</th>
                            <th scope="col">Batch</th>
                            <th scope="col">Status</th>
                            <th scope="col">Admission</th>
                            <th scope="col">Action</th>
                          </tr>
                        </thead>
                        <tbody aria-busy={ticketLoading}>
                          {ticketPage.rows.map((ticket) => (
                            <tr key={ticket.id}>
                              <td data-label="Ticket">
                                <strong>#{ticket.display_number}</strong>
                                <small>{ticket.manual_code}</small>
                              </td>
                              <td data-label="Batch">{ticket.batch_name}</td>
                              <td data-label="Status">
                                <span className={`ticket-state ${ticket.status}`}>
                                  {ticket.status === 'used'
                                    ? 'Admitted'
                                    : ticket.status === 'unused'
                                      ? 'Ready'
                                      : 'Voided'}
                                </span>
                              </td>
                              <td data-label="Admission">
                                {ticket.used_at ? (
                                  <>
                                    <time
                                      dateTime={ticket.used_at}
                                      title={new Date(ticket.used_at).toLocaleString()}
                                    >
                                      {relativeTime(ticket.used_at)}
                                    </time>
                                    <small>{ticket.used_by_device_name ?? 'Unknown scanner'}</small>
                                  </>
                                ) : (
                                  <span className="muted-value">—</span>
                                )}
                              </td>
                              <td data-label="Action">
                                {ticket.status === 'used' ? (
                                  <button
                                    className="text-button reset-ticket-button"
                                    type="button"
                                    aria-label={`Reset admission for ticket ${ticket.display_number}`}
                                    onClick={() =>
                                      setConfirmation({ kind: 'reset-ticket', ticket })
                                    }
                                    disabled={busy}
                                  >
                                    Reset admission
                                  </button>
                                ) : (
                                  <span className="muted-value">—</span>
                                )}
                              </td>
                            </tr>
                          ))}
                          {!ticketLoading && !ticketPage.rows.length && (
                            <tr>
                              <td colSpan={5}>
                                <div className="empty-state compact">
                                  <strong>No matching tickets</strong>
                                  <span>
                                    Change the search or status filter to see more tickets.
                                  </span>
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    <div className="ticket-pagination">
                      <span>
                        {ticketPage.total
                          ? `${(ticketCursor + 1).toLocaleString()}–${(
                              ticketCursor + ticketPage.rows.length
                            ).toLocaleString()} of ${ticketPage.total.toLocaleString()}`
                          : '0 tickets'}
                      </span>
                      <div>
                        <button
                          className="quiet-button"
                          type="button"
                          onClick={() =>
                            setTicketCursor((current) => Math.max(0, current - ticketPageSize))
                          }
                          disabled={ticketLoading || ticketCursor === 0}
                        >
                          Previous
                        </button>
                        <button
                          className="quiet-button"
                          type="button"
                          onClick={() => {
                            if (ticketPage.nextCursor !== null) {
                              setTicketCursor(ticketPage.nextCursor);
                            }
                          }}
                          disabled={ticketLoading || ticketPage.nextCursor === null}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </section>
                )}

                <section className="operation-section batch-section">
                  <div className="section-heading">
                    <div>
                      <p className="eyebrow">Ticket inventory</p>
                      <h3>Batches and downloads</h3>
                      <p className="supporting">
                        Generate numbered tickets, then download only the files you need.
                      </p>
                    </div>
                  </div>
                  <form className="batch-form" onSubmit={(event) => void createBatch(event)}>
                    <label>
                      Batch name
                      <input name="name" placeholder="General admission" required />
                    </label>
                    <label>
                      Quantity
                      <input
                        name="quantity"
                        type="number"
                        min="1"
                        max="1000000"
                        defaultValue="100"
                        required
                      />
                    </label>
                    <button
                      type="submit"
                      disabled={busy || !selectedId || selected?.status === 'closed'}
                    >
                      {busy ? 'Working…' : 'Generate batch'}
                    </button>
                  </form>
                  {selected?.status === 'closed' && (
                    <p className="inline-warning">Closed events cannot generate more tickets.</p>
                  )}
                  <div className="table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th scope="col">Batch</th>
                          <th scope="col">Generated</th>
                          <th scope="col">Unused</th>
                          <th scope="col">Admitted</th>
                          <th scope="col">Downloads</th>
                        </tr>
                      </thead>
                      <tbody>
                        {batches.map((batch) => {
                          const progress = Math.round(
                            (batch.generated_quantity / Math.max(1, batch.quantity)) * 100
                          );
                          return (
                            <tr key={batch.id}>
                              <td data-label="Batch">
                                <strong>{batch.name}</strong>
                                <small>{batch.id}</small>
                              </td>
                              <td data-label="Generated">
                                <span>
                                  {batch.generated_quantity.toLocaleString()} /{' '}
                                  {batch.quantity.toLocaleString()}
                                </span>
                                <progress
                                  aria-label={`${batch.name} generation progress`}
                                  value={batch.generated_quantity}
                                  max={batch.quantity}
                                />
                                <small>{progress}% complete</small>
                              </td>
                              <td data-label="Unused">{Number(batch.unused).toLocaleString()}</td>
                              <td data-label="Admitted">{Number(batch.used).toLocaleString()}</td>
                              <td data-label="Downloads">
                                <div className="export-actions">
                                  {(
                                    [
                                      {
                                        format: 'pdf' as const,
                                        label: 'PDF · 8-up',
                                        ariaLabel: `Download ${batch.name} as PDF`
                                      },
                                      {
                                        format: 'pdf-a4-four-up' as const,
                                        label: 'A4 · 4-up',
                                        ariaLabel: `Download ${batch.name} as A4 PDF with 4 tickets per sheet`
                                      },
                                      {
                                        format: 'seller-form' as const,
                                        label: 'Seller form',
                                        ariaLabel: `Download ${batch.name} printable seller form`
                                      },
                                      {
                                        format: 'png' as const,
                                        label: 'PNG ZIP',
                                        ariaLabel: `Download ${batch.name} as PNG ZIP`
                                      },
                                      {
                                        format: 'svg' as const,
                                        label: 'SVG ZIP',
                                        ariaLabel: `Download ${batch.name} as SVG ZIP`
                                      }
                                    ] as const
                                  ).map(({ format, label, ariaLabel }) => (
                                    <button
                                      className="text-button"
                                      key={format}
                                      onClick={() => void exportBatch(batch, format)}
                                      disabled={busy || batch.generated_quantity === 0}
                                      aria-label={ariaLabel}
                                    >
                                      {label}
                                    </button>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                        {!batches.length && (
                          <tr>
                            <td colSpan={5}>
                              <div className="empty-state compact">
                                <strong>No batches yet</strong>
                                <span>Create the first numbered ticket batch above.</span>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <p className="supporting export-note">
                    Choose compact PDF sheets (8 per page), larger A4 landscape sheets (4 per page),
                    a printable seller form, or PNG and SVG postcard ticket ZIPs. Keep individual
                    batches at 500 tickets or fewer for reliable browser exports. Every ticket
                    includes its written manual admission code; seller forms contain ticket numbers,
                    their printed ticket references and blank buyer-detail fields.
                  </p>
                </section>
              </section>

              <aside className="secondary-column">
                {adminSession?.role === 'super_admin' && (
                  <section className="admin-access" aria-labelledby="admin-access-title">
                    <p className="eyebrow">Super admin</p>
                    <h3 id="admin-access-title">Administrator access</h3>
                    <p className="supporting">
                      Allow people by email and choose what they can delete. They only receive
                      access to this website—never the Cloudflare dashboard.
                    </p>
                    <form
                      className="admin-invite-form"
                      onSubmit={(event) => void addAdministrator(event)}
                    >
                      <label>
                        Email address
                        <input
                          name="email"
                          type="email"
                          placeholder="staff@example.com"
                          maxLength={320}
                          autoComplete="email"
                          required
                        />
                      </label>
                      <label>
                        Role
                        <select name="role" defaultValue="admin">
                          <option value="admin">Admin</option>
                          <option value="super_admin">Super admin</option>
                        </select>
                      </label>
                      <button type="submit" disabled={busy}>
                        Add administrator
                      </button>
                    </form>
                    <div className="admin-user-list">
                      {adminUsers
                        .filter((administrator) => !administrator.revoked_at)
                        .map((administrator) => {
                          const currentUser = administrator.email === adminSession.identity;
                          return (
                            <div className="admin-user" key={administrator.email}>
                              <div>
                                <strong title={administrator.email}>{administrator.email}</strong>
                                <span>
                                  {currentUser ? 'You · ' : ''}
                                  {roleLabel(administrator.role)}
                                </span>
                              </div>
                              <div className="admin-user-actions">
                                <select
                                  aria-label={`Role for ${administrator.email}`}
                                  value={administrator.role}
                                  disabled={busy || currentUser}
                                  onChange={(event) =>
                                    void changeAdministratorRole(
                                      administrator,
                                      event.target.value as AdminRole
                                    )
                                  }
                                >
                                  <option value="admin">Admin</option>
                                  <option value="super_admin">Super admin</option>
                                </select>
                                {!currentUser && (
                                  <button
                                    className="text-button danger-text"
                                    type="button"
                                    onClick={() =>
                                      setConfirmation({
                                        kind: 'remove-admin',
                                        administrator
                                      })
                                    }
                                    disabled={busy}
                                  >
                                    Remove
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </section>
                )}
                <section>
                  <p className="eyebrow">Scanner access</p>
                  <h3>Entrance devices</h3>
                  <div className="scanner-installer">
                    <div>
                      <strong>Install the Android scanner</strong>
                      <span>
                        Send the signed app link to event staff, then create a separate activation
                        code for each device.
                      </span>
                    </div>
                    <div className="scanner-installer-actions">
                      <a
                        className="text-button"
                        href={scannerDownloadUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Download APK
                      </a>
                      <button
                        className="text-button"
                        type="button"
                        onClick={() => {
                          void navigator.clipboard
                            .writeText(scannerDownloadUrl)
                            .then(() => setNotice('Scanner app download link copied.'))
                            .catch(() =>
                              setNotice('Copy failed. Open the download and copy its address.')
                            );
                        }}
                      >
                        Copy download link
                      </button>
                    </div>
                  </div>
                  <p className="supporting">
                    Each scanner needs its own eight-character, single-use code. Hyphens and spaces
                    are optional; codes expire after 30 minutes.
                  </p>
                  <button
                    onClick={() => void createActivationCode()}
                    disabled={busy || selected?.status !== 'active'}
                  >
                    Create activation code
                  </button>
                  {selected?.status !== 'active' && (
                    <p className="inline-warning">Activate the event before adding scanners.</p>
                  )}
                  {activationCode && (
                    <div className="activation-panel">
                      <output className="activation-code" aria-label="Scanner activation code">
                        {activationCode}
                      </output>
                      <div>
                        <span>Expires {new Date(activationExpiresAt).toLocaleTimeString()}</span>
                        <button
                          className="text-button"
                          onClick={() => {
                            void navigator.clipboard
                              .writeText(activationCode)
                              .then(() => setNotice('Activation code copied.'))
                              .catch(() => setNotice('Copy failed. Select the code manually.'));
                          }}
                        >
                          Copy code
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="device-list">
                    {devices.map((device) => (
                      <div
                        key={device.id}
                        className={device.revoked_at ? 'device revoked' : 'device'}
                      >
                        <div>
                          <strong>{device.name}</strong>
                          <small>
                            {device.revoked_at
                              ? 'Revoked'
                              : `Last seen ${relativeTime(device.last_seen_at)}`}
                          </small>
                        </div>
                        {!device.revoked_at && (
                          <button
                            className="text-button danger-text"
                            onClick={() => setConfirmation({ kind: 'revoke-device', device })}
                          >
                            Revoke
                          </button>
                        )}
                      </div>
                    ))}
                    {!devices.length && (
                      <div className="empty-state compact">
                        <strong>No scanners connected</strong>
                        <span>Create a code when the event is active.</span>
                      </div>
                    )}
                  </div>
                </section>

                <section className="launch-checklist" aria-labelledby="launch-checklist-title">
                  <p className="eyebrow">Event readiness</p>
                  <h3 id="launch-checklist-title">Opening checklist</h3>
                  <ol>
                    <li className={batches.length > 0 ? 'complete' : ''}>
                      <span aria-hidden="true">{batches.length > 0 ? '✓' : '1'}</span>
                      Generate a ticket batch
                    </li>
                    <li className={selected?.status === 'active' ? 'complete' : ''}>
                      <span aria-hidden="true">{selected?.status === 'active' ? '✓' : '2'}</span>
                      Activate the event
                    </li>
                    <li className={devices.some((device) => !device.revoked_at) ? 'complete' : ''}>
                      <span aria-hidden="true">
                        {devices.some((device) => !device.revoked_at) ? '✓' : '3'}
                      </span>
                      Connect an entrance scanner
                    </li>
                  </ol>
                </section>

                <section className="team-activity" aria-labelledby="team-activity-title">
                  <p className="eyebrow">Accountability</p>
                  <h3 id="team-activity-title">Team activity</h3>
                  <p className="supporting">
                    Every administrator change is recorded against the verified login.
                  </p>
                  <div className="audit-list">
                    {auditRecords.slice(0, 8).map((record) => (
                      <div className="audit-entry" key={record.id}>
                        <span
                          className={`audit-outcome ${record.outcome}`}
                          role="img"
                          aria-label={`Outcome: ${record.outcome}`}
                        />
                        <div>
                          <strong>{auditActionLabel(record.action)}</strong>
                          <span>
                            {identityLabel(record.actor_identity)} ·{' '}
                            <time
                              dateTime={record.created_at}
                              title={new Date(record.created_at).toLocaleString()}
                            >
                              {relativeTime(record.created_at)}
                            </time>
                          </span>
                        </div>
                      </div>
                    ))}
                    {!auditRecords.length && (
                      <div className="empty-state compact">
                        <strong>No changes recorded yet</strong>
                        <span>Administrator actions will appear here.</span>
                      </div>
                    )}
                  </div>
                </section>
              </aside>
            </div>
          </>
        )}
      </main>

      {confirmation && (
        <div className="dialog-backdrop">
          <section
            className="confirmation-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="confirmation-title"
            aria-describedby="confirmation-description"
          >
            <p className="eyebrow">Confirm action</p>
            <h3 id="confirmation-title">
              {confirmation.kind === 'close-event'
                ? `Close ${selected?.name ?? 'this event'}?`
                : confirmation.kind === 'delete-draft' || confirmation.kind === 'delete-event'
                  ? `Delete ${selected?.name ?? 'this event'}?`
                  : confirmation.kind === 'reset-ticket'
                    ? `Reset ticket #${confirmation.ticket.display_number}?`
                    : confirmation.kind === 'remove-admin'
                      ? `Remove ${confirmation.administrator.email}?`
                      : `Revoke ${confirmation.device.name}?`}
            </h3>
            <p id="confirmation-description">
              {confirmation.kind === 'close-event'
                ? 'Admissions will stop immediately and the event cannot be reopened from this dashboard.'
                : confirmation.kind === 'delete-draft'
                  ? 'This permanently removes the draft, its batches, and every unused ticket generated for it. Type the event name to confirm.'
                  : confirmation.kind === 'delete-event'
                    ? 'This permanently removes the event, all tickets, scanner connections, and admission history. This cannot be undone. Type the event name to confirm.'
                    : confirmation.kind === 'reset-ticket'
                      ? 'This makes the ticket ready to admit again. Its previous scanner attempt remains in recent activity and the administrator audit history.'
                      : confirmation.kind === 'remove-admin'
                        ? 'Their website access is removed immediately. Their previous actions remain in the audit history.'
                        : 'This scanner will be signed out and cannot validate another ticket until it receives a new activation code.'}
            </p>
            {(confirmation.kind === 'delete-draft' || confirmation.kind === 'delete-event') && (
              <label className="delete-confirmation">
                Event name
                <input
                  autoFocus
                  value={deleteConfirmationName}
                  onChange={(event) => setDeleteConfirmationName(event.target.value)}
                  placeholder={selected?.name}
                  autoComplete="off"
                />
              </label>
            )}
            <div className="dialog-actions">
              <button
                className="quiet-button"
                autoFocus={
                  confirmation.kind !== 'delete-draft' && confirmation.kind !== 'delete-event'
                }
                onClick={() => {
                  setConfirmation(null);
                  setDeleteConfirmationName('');
                }}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                className={
                  confirmation.kind === 'reset-ticket' ? 'warning-button' : 'danger-button'
                }
                onClick={() => {
                  if (confirmation.kind === 'close-event') {
                    void changeEvent('close').finally(() => setConfirmation(null));
                  } else if (
                    confirmation.kind === 'delete-draft' ||
                    confirmation.kind === 'delete-event'
                  ) {
                    void deleteEvent();
                  } else if (confirmation.kind === 'remove-admin') {
                    void removeAdministrator(confirmation.administrator);
                  } else if (confirmation.kind === 'reset-ticket') {
                    void resetTicket(confirmation.ticket);
                  } else {
                    void revokeDevice(confirmation.device);
                  }
                }}
                disabled={
                  busy ||
                  ((confirmation.kind === 'delete-draft' || confirmation.kind === 'delete-event') &&
                    deleteConfirmationName.trim() !== selected?.name)
                }
              >
                {confirmation.kind === 'close-event'
                  ? 'Close event'
                  : confirmation.kind === 'delete-draft'
                    ? 'Delete draft'
                    : confirmation.kind === 'delete-event'
                      ? 'Delete event permanently'
                      : confirmation.kind === 'reset-ticket'
                        ? 'Reset admission'
                        : confirmation.kind === 'remove-admin'
                          ? 'Remove access'
                          : 'Revoke scanner'}
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
